import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import _, { cloneDeep, merge } from 'lodash';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Typography from '~/components/core/Atomic/Typography';
import InnerCard from '~/components/core/Cards/InnerCard';
import CheckboxWithButtonWrapperFormik from '~/components/core/Formik/CheckboxWithButtonWrapperFormik';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import Switch from '~/components/ui/atoms/Switch';
import cn from '~/Utils/cn';

import {
  CONFIGURATION_FEATURES_NAMES,
  PAYMENT_ADDITIONAL_REQUIRED_FIELDS,
  PAYMENT_METHOD_CONFIGURATION_DEFAULT_V3,
  PAYMENT_METHODS_DETAILS_DICT,
} from '../../../../Types';
import { isFeatureEnabled } from '../../../../Utils';
import CardDialog from '../../../CardDialog';
import { ErrorHelperTextFormik } from '../../../core/Formik/ErrorHelperTextFormik';
import ArrayFieldFormik from '../../../core/Formik/NestedFieldFormik/ArrayFieldFormik';
import { PAY_TO_LENGTH_VALIDATION_FIELDS } from '../../../exposures/PaymentRequestContainer/paymentContainerUtils';
import getOneIncMethodSpecificPaymentConfig from '../../../OneInc/MethodSpecificConfiguration';
import TextFieldFormik, { NumericTextFieldFormik } from '../../../TextFieldFormik';
import { CoveragesMultiselectFormik } from '../../../TPA/Coverages/CoveragesMultiselectFormik';
import { useSysconfig } from '../../SystemConfigurationScreen';

import { getDefaultFundAccountConfig, isFundAccountResolvedByRules } from './FundAccountSourceField';
import PAYMENT_PROVIDERS_CONFIG from './paymentProviders';
import PaymentProviderSelect from './PaymentProviderSelect';
import SectionHeading from './SectionHeading';

import { useStyles } from '../../../../assets/styles';
import styles from './PaymentsConfigurationForm.module.scss';

// The key prop for the following const is used when converting from an object to array and vice versa
const EXPENSES_LABELS_NESTED_FIELD_CONFIG = [
  { id: 'key', label: 'Type Id', type: 'text', disableEditAfterInitialSet: true },
  { id: 'desc', label: 'Type name', type: 'text' },
  { id: 'is_removed', label: 'Is removed', type: 'switch', additionalProps: { hideErrorGap: true } },
];

const INDEMNITY_LABELS_NESTED_FIELD_CONFIG = [
  { id: 'key', label: 'Type Id', type: 'text', disableEditAfterInitialSet: true },
  { id: 'desc', label: 'Type name', type: 'text' },
  { id: 'is_removed', label: 'Is removed', type: 'switch', additionalProps: { hideErrorGap: true } },
];

const INNER_FIELD_INITIAL_VALUES = { key: '', desc: '', is_removed: false };

const PaymentsConfigurationForm = ({
  isDialogOpen,
  readOnly,
  onClose,
  handleFormSubmit,
  configValues,
  isHomeLobEnabledForOrg,
  paymentMethodName,
  isLastEnabledPaymentMethod,
  isDialog = true,
  paymentProvidersConfigured,
}) => {
  const { classes } = useStyles();
  const { organization } = useSysconfig();
  const [requiredFieldsForced, setRequiredFieldsForced] = React.useState([]);

  const isConfigurableCoveragesEnabled = isFeatureEnabled(
    organization,
    CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_COVERAGES
  );

  const isConfigurableFnolEnabled = isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL);
  const isForceMortgageeEnabled = PAYMENT_METHODS_DETAILS_DICT[paymentMethodName]?.include_force_mortgagee_option;
  const shouldDisplayForceMortgageeOptions =
    isHomeLobEnabledForOrg && isForceMortgageeEnabled && !isConfigurableFnolEnabled;

  const getDefaultMethodSpecificPaymentConfig = () => {
    const methodSpecificInitialValues = {};
    const methodSpecificValidationSchema = Yup.object();
    const MethodSpecificPaymentConfigComponent = null;
    return {
      methodSpecificInitialValues,
      methodSpecificValidationSchema,
      MethodSpecificPaymentConfigComponent,
    };
  };

  const isOneIncPayment = paymentMethodName === 'one_inc';
  const getMethodSpecificPaymentConfig = isOneIncPayment
    ? getOneIncMethodSpecificPaymentConfig
    : getDefaultMethodSpecificPaymentConfig;
  const { methodSpecificInitialValues, methodSpecificValidationSchema, MethodSpecificPaymentConfigComponent } =
    getMethodSpecificPaymentConfig();

  const firstSectionSwitches = [
    { id: 'enable_record_only', text: 'Include "Record only" checkbox' },
    { id: 'enable_reissued', text: 'Include "Reissue payment" checkbox' },
    {
      id: 'enable_documents',
      text: 'Include Document Attachments',
      isDisabled: (paymentMethod) => PAYMENT_METHODS_DETAILS_DICT[paymentMethod]?.always_enable_documents,
    },
    { id: 'permissions_to_send_communication_required', text: 'Permissions to send sms and email are required' },
    {
      id: 'enable_cancel_pending',
      text: 'Enable cancel pending behavior',
      isDisabled: (paymentMethod) =>
        PAYMENT_METHODS_DETAILS_DICT[paymentMethod]?.always_move_to_pending_cancel_upon_cancellation,
    },
  ];

  const ADDITIONAL_SETTINGS_SWITCHES = [
    {
      title: '',
      isActive: () => true,
      switches: firstSectionSwitches,
    },
    {
      title: '',
      isActive: (paymentMethod) => PAYMENT_METHODS_DETAILS_DICT[paymentMethod]?.reference?.should_include,
      switches: [
        {
          id: 'prefill_indemnity_reference_with_claim_number',
          text: 'Pre-fill indemnity payment reference with "Claim number"',
        },
        {
          id: 'prefill_expenses_reference_with_invoice_number',
          text: 'For expense payments use "Invoice number" as the reference by default',
        },
      ],
    },
    {
      title: 'Only for home LOB:',
      isActive: () => shouldDisplayForceMortgageeOptions,
      switches: [{ id: 'home_only_should_force_mortgagee', text: 'Set mortgagee as a payee in payment request:' }],
    },
    {
      title: '',
      isActive: (paymentMethod) => !!PAYMENT_METHODS_DETAILS_DICT[paymentMethod]?.contact_bank_fields,
      switches: [
        {
          id: 'use_contact_details_readonly',
          text: "Use contact's bank details (read-only)",
        },
      ],
    },
    {
      title: '',
      isActive: (paymentMethod) => !!PAYMENT_METHODS_DETAILS_DICT[paymentMethod]?.swift_bic,
      switches: [{ id: 'swift_bic.should_include', text: 'Include SWIFT/BIC field' }],
    },
    {
      title: '',
      isActive: (paymentMethod) => paymentMethod === 'nz_bank_transfer',
      switches: [{ id: 'nz_bank_account_exact_16_digits', text: 'Require exactly 16 digits (NZ bank account)' }],
    },
  ];

  const getInitialValues = () => {
    const defaultConfig = cloneDeep(PAYMENT_METHOD_CONFIGURATION_DEFAULT_V3);
    const defaultMethodSpecificConfig = { method_configuration: { method_specific: methodSpecificInitialValues } };
    // A merge *layer* rather than a post-merge default, so a persisted value wins: an organization
    // saved on the legacy Vitesse API keeps it. The placement is load-bearing — an explicitly null
    // `payment_provider_specific` must keep winning over the default, which is what lodash `merge`
    // does here and what applying the default afterwards would not.
    const savedProvider = configValues.method_configuration?.payment_provider;
    const defaultProviderSpecificConfig =
      PAYMENT_PROVIDERS_CONFIG[savedProvider]?.getDefaultProviderSpecific?.({
        paymentMethodName,
        paymentProvider: savedProvider,
      }) || {};
    if (PAYMENT_METHODS_DETAILS_DICT[paymentMethodName]?.always_enable_documents) {
      configValues.method_configuration.enable_documents = true;
    }
    if (PAYMENT_METHODS_DETAILS_DICT[paymentMethodName]?.always_require_permissions_to_send_communication) {
      configValues.method_configuration.permissions_to_send_communication_required = true;
    }
    if (PAYMENT_METHODS_DETAILS_DICT[paymentMethodName]?.always_move_to_pending_cancel_upon_cancellation) {
      configValues.method_configuration.enable_cancel_pending = true;
    }
    if (!configValues.method_configuration.swift_bic && PAYMENT_METHODS_DETAILS_DICT[paymentMethodName]?.swift_bic) {
      configValues.method_configuration.swift_bic = {
        should_include: PAYMENT_METHODS_DETAILS_DICT[paymentMethodName].swift_bic.should_include ?? true,
      };
    }
    // The same treatment for the generic fund-account block, and for the same reason: a persisted block
    // has to win, and a configuration that has never had one has to read as what it actually does today
    // — the provider sending from its own configured account — rather than as an unanswered radio group.
    // Keyed on the *saved* provider, so this seeds only what the mounted radio can then edit.
    const defaultFundAccountConfig = getDefaultFundAccountConfig({ organization, paymentProvider: savedProvider });
    const initialValues = merge(
      defaultConfig,
      defaultMethodSpecificConfig,
      defaultProviderSpecificConfig,
      defaultFundAccountConfig,
      configValues
    );
    const withIncomingDefaults =
      PAYMENT_PROVIDERS_CONFIG[initialValues.method_configuration.payment_provider]?.withIncomingDefaults;
    if (withIncomingDefaults) {
      initialValues.method_configuration.payment_provider_specific = withIncomingDefaults(
        initialValues.method_configuration.payment_provider_specific,
        { paymentMethodName, isCustomMethod }
      );
    }
    return initialValues;
  };

  const validateMailEnabledForCurrentPaymentMethod = (currentPaymentMethod) => {
    return Boolean(
      currentPaymentMethod && PAYMENT_METHODS_DETAILS_DICT[currentPaymentMethod]?.mail_to_contact?.should_include
    );
  };
  const isUsingCustomIndemnityLabels = (values) => values.method_configuration.use_indemnity_labels;
  const isUsingCustomExpensesLabels = (values) => values.method_configuration.use_expenses_labels;

  const isCustomMethod = PAYMENT_METHODS_DETAILS_DICT[paymentMethodName]?.is_custom_method;
  const labelsNestedScheme = Yup.object().shape({
    key: Yup.string().required('Required'),
    desc: Yup.string().required('Required'),
    is_removed: Yup.boolean(),
  });

  const baseConfigValidationScheme = Yup.object().shape({
    mail_to_limits: Yup.object().shape(
      Object.fromEntries(PAY_TO_LENGTH_VALIDATION_FIELDS.map(({ maxCharsKey }) => [maxCharsKey, Yup.number()]))
    ),
    enable_record_only: Yup.boolean(),
    enable_reissued: Yup.boolean(),
    nz_bank_account_exact_16_digits: Yup.boolean(),
    use_expenses_labels: Yup.boolean(),
    expenses_labels: Yup.array()
      .of(labelsNestedScheme)
      .nullable()
      .when('use_expenses_labels', {
        is: true,
        then: (schema) =>
          schema.test(
            'is-valid',
            'At least 1 active label is required',
            (value) => !value || value.filter(({ is_removed }) => !is_removed).length > 0
          ),
        otherwise: (schema) => schema,
      }),
    use_indemnity_labels: Yup.boolean(),
    indemnity_labels: Yup.array()
      .of(labelsNestedScheme)
      .nullable()
      .when('use_indemnity_labels', {
        is: true,
        then: (schema) =>
          schema.test(
            'is-valid',
            'At least 1 active label is required',
            (value) => !value || value.filter(({ is_removed }) => !is_removed).length > 0
          ),
        otherwise: (schema) => schema,
      }),
    prefill_indemnity_reference_with_claim_number: Yup.boolean(),
    prefill_expenses_reference_with_invoice_number: Yup.boolean(),
    home_only_should_force_mortgagee: Yup.boolean(),
    force_mortgagee_options: Yup.object().when('home_only_should_force_mortgagee', {
      is: true,
      then: (schema) =>
        schema.shape({
          coverage_keys: Yup.array().of(Yup.string()).min(1, 'At least one coverage is required'),
          amount: Yup.number().required('Required'),
        }),
      otherwise: (schema) => schema,
    }),
    required_fields: Yup.lazy((value) => {
      const schema = {};

      if (value) {
        Object.keys(value).map((key) => (schema[key] = Yup.boolean()));
      }

      return Yup.object().shape(schema);
    }),
    method_specific: methodSpecificValidationSchema,
    // The selected provider's own rules, spread in by its descriptor. A provider that stores no
    // `payment_provider_specific` document contributes none, and the object stays unconstrained.
    //
    // Conditioned on the fund-account source as well as on the provider: the block is a *sibling* of this
    // one under `method_configuration`, and it decides whether the provider's own account is the account
    // being used at all. Yup 0.32 hands a multi-key `when` its values spread, ahead of the schema.
    payment_provider_specific: Yup.object().when(
      ['payment_provider', 'fund_account'],
      (paymentProvider, fundAccount, schema) => {
        const providerShape = PAYMENT_PROVIDERS_CONFIG[paymentProvider]?.getValidationShape?.({
          isCustomMethod,
          paymentMethodName,
          isFundAccountSourceRules: isFundAccountResolvedByRules({ organization, fundAccount }),
        });
        return providerShape ? schema.shape(providerShape) : schema;
      }
    ),
  });

  return (
    <Formik
      initialValues={getInitialValues()}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const withOutgoingValues =
            PAYMENT_PROVIDERS_CONFIG[values.method_configuration?.payment_provider]?.withOutgoingValues;
          const outgoingValues = withOutgoingValues
            ? withOutgoingValues(values, { paymentMethodName, isCustomMethod })
            : values;
          await handleFormSubmit(outgoingValues, paymentMethodName);
        } catch (e) {
          setSubmitting(false);
        }
      }}
      validationSchema={Yup.object().shape({
        payment_method_external_id: isCustomMethod
          ? Yup.string()
              .matches(/^[a-zA-Z0-9_-]*$/, 'Can only contain letters, numbers and the following special characters: _-')
              .required('Required')
          : null,
        payment_method_display_name: isCustomMethod ? Yup.string().required('Required') : undefined,
        method_configuration: baseConfigValidationScheme,
      })}
    >
      {(formikProps) => {
        const { isSubmitting, handleSubmit, values, setFieldValue } = formikProps;
        const currentPaymentMethod = paymentMethodName;

        return (
          <CardDialog
            isDialog={isDialog}
            open={isDialogOpen}
            title={`${PAYMENT_METHODS_DETAILS_DICT[currentPaymentMethod].display_name} Method Configuration`}
            onClose={onClose}
            maxWidth="md"
          >
            <Grid container spacing={2} className={styles.mainContainer}>
              <div className={cn(styles.switchContainer, styles.section)}>
                <Switch
                  checked={!values.is_disabled}
                  onChange={() => {
                    setFieldValue('is_disabled', !values.is_disabled);
                  }}
                  disabled={isLastEnabledPaymentMethod || isSubmitting}
                />
                <span>{values.is_disabled ? 'Disabled' : 'Enabled'}</span>
              </div>

              {MethodSpecificPaymentConfigComponent && (
                <Grid container spacing={4} className={styles.section}>
                  <SectionHeading
                    title={`${PAYMENT_METHODS_DETAILS_DICT[currentPaymentMethod].display_name} Method Details`}
                    subtitle=""
                  />
                  <MethodSpecificPaymentConfigComponent />
                </Grid>
              )}

              {PAYMENT_METHODS_DETAILS_DICT[currentPaymentMethod].is_custom_method && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextFieldFormik
                      className={classes.textField}
                      id="payment_method_external_id"
                      label="Method ID"
                      disabled={readOnly || isSubmitting}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextFieldFormik
                      className={classes.textField}
                      id="payment_method_display_name"
                      label="Method Name"
                      disabled={readOnly || isSubmitting}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              )}

              <PaymentProviderSelect
                paymentProvidersConfigured={paymentProvidersConfigured}
                currentPaymentMethod={currentPaymentMethod}
                isCustomMethod={isCustomMethod}
                requiredFieldsForced={requiredFieldsForced}
                setRequiredFieldsForced={setRequiredFieldsForced}
                readOnly={readOnly}
              />

              {_.difference(
                Object.keys(PAYMENT_ADDITIONAL_REQUIRED_FIELDS),
                PAYMENT_METHODS_DETAILS_DICT[currentPaymentMethod].excluded_required_fields || []
              ).length > 0 && (
                <InnerCard className={styles.section}>
                  <SectionHeading
                    title="Required Fields"
                    subtitle="Select which field is mandatory to fill in when creating a new payment. Optional to include both fields."
                  />
                  <Grid container spacing={1} className={styles.section}>
                    {Object.keys(PAYMENT_ADDITIONAL_REQUIRED_FIELDS).map((required_field) => (
                      <>
                        {!(PAYMENT_METHODS_DETAILS_DICT[currentPaymentMethod].excluded_required_fields || []).includes(
                          required_field
                        ) && (
                          <Grid key={required_field} item xs={3}>
                            <CheckboxWithButtonWrapperFormik
                              id={`method_configuration.required_fields.${required_field}`}
                              text={PAYMENT_ADDITIONAL_REQUIRED_FIELDS[required_field].desc}
                              disabled={readOnly || isSubmitting || requiredFieldsForced.includes(required_field)}
                              className={styles.checkbox}
                            />
                          </Grid>
                        )}
                      </>
                    ))}
                  </Grid>
                </InnerCard>
              )}

              {currentPaymentMethod && (
                <>
                  {validateMailEnabledForCurrentPaymentMethod(currentPaymentMethod) && (
                    <Grid container spacing={4} className={styles.section}>
                      <SectionHeading title="Mail fields max lengths" subtitle="" />
                      {PAY_TO_LENGTH_VALIDATION_FIELDS.map(({ maxCharsKey, description }) => (
                        <Grid item xs={6} key={String(maxCharsKey)}>
                          <NumericTextFieldFormik
                            id={`method_configuration.mail_to_limits.${maxCharsKey}`}
                            label={description}
                            showOnly={readOnly || isSubmitting}
                            fullWidth
                          />
                          <ErrorHelperTextFormik id={`method_configuration.mail_to_limits.${maxCharsKey}`} />
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  <InnerCard className={styles.section}>
                    <SectionHeading title="Expenses Payment Types" subtitle="" className={styles.section} />
                    <Grid item xs={12}>
                      <SwitchFormik
                        id="method_configuration.use_expenses_labels"
                        label="Set expenses types"
                        showOnly={readOnly || isSubmitting}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {isUsingCustomExpensesLabels(values) ? (
                        <>
                          <ArrayFieldFormik
                            fieldId="method_configuration.expenses_labels"
                            label="Expenses labels"
                            innerObjectConfig={EXPENSES_LABELS_NESTED_FIELD_CONFIG}
                            addNewItemButtonText="Add new expenses label +"
                            disabled={readOnly || isSubmitting}
                            initialNewItemValues={INNER_FIELD_INITIAL_VALUES}
                            allowDeletingInitialValues={false}
                          />
                          <ErrorHelperTextFormik id="method_configuration.expenses_labels" withoutChildren />
                        </>
                      ) : null}
                    </Grid>
                  </InnerCard>

                  <InnerCard className={styles.section}>
                    <SectionHeading
                      title="Indemnity Payment Types"
                      subtitle="Optional to add labels to Indemnity payments, will appear in as a dropdown when making a payment"
                    />
                    <Grid item xs={12}>
                      <SwitchFormik
                        id="method_configuration.use_indemnity_labels"
                        label="Set indemnity types"
                        showOnly={readOnly || isSubmitting}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {isUsingCustomIndemnityLabels(values) && (
                        <>
                          <ArrayFieldFormik
                            fieldId="method_configuration.indemnity_labels"
                            label="Indemnity labels"
                            innerObjectConfig={INDEMNITY_LABELS_NESTED_FIELD_CONFIG}
                            addNewItemButtonText="Add new indemnity label +"
                            disabled={readOnly || isSubmitting}
                            initialNewItemValues={INNER_FIELD_INITIAL_VALUES}
                            allowDeletingInitialValues={false}
                          />
                          <ErrorHelperTextFormik id="method_configuration.indemnity_labels" withoutChildren />
                        </>
                      )}
                    </Grid>
                  </InnerCard>

                  <div className={styles.section}>
                    <SectionHeading title="Additional Settings" />
                    {ADDITIONAL_SETTINGS_SWITCHES.map(({ title, switches, isActive }, idx) => (
                      <Fragment key={idx}>
                        {isActive(currentPaymentMethod) && (
                          <Grid item xs={12} key={title}>
                            <Typography variant="subtitle1">{title}</Typography>
                            {switches.map(({ id, text, isDisabled }) => (
                              <Grid item xs={12} key={id}>
                                <SwitchFormik
                                  id={`method_configuration.${id}`}
                                  label={text}
                                  key={id}
                                  showOnly={readOnly}
                                  disabled={isSubmitting || (isDisabled && isDisabled(currentPaymentMethod))}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </Fragment>
                    ))}
                  </div>

                  {shouldDisplayForceMortgageeOptions && (
                    <Grid direction="row" container spacing={2} style={{ margin: '-14px 0 20px 12px' }}>
                      <Grid item xs={6}>
                        <NumericTextFieldFormik
                          id="method_configuration.force_mortgagee_options.amount"
                          label="Amount above"
                          fullWidth
                          disabled={readOnly || !isHomeLobEnabledForOrg || isSubmitting}
                        />
                      </Grid>
                      <Grid item xs={6} style={{ marginTop: '-3px' }}>
                        <CoveragesMultiselectFormik
                          lobs={['home_claim']}
                          label="For Coverages"
                          subOrganizationIds={[]}
                          coveragesFieldId="method_configuration.force_mortgagee_options.coverage_keys"
                          disabled={readOnly || !isHomeLobEnabledForOrg || isSubmitting}
                          disableGeneral={true}
                          useOrgLevelCoveragesOnly={isConfigurableCoveragesEnabled}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {!readOnly && (
                    <div className={classes.buttonsContainer}>
                      <Button
                        variant="contained"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className={classes.cancelButtonWithRoundMargin}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        onClick={handleSubmit}
                        className={classes.button}
                      >
                        Save
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Grid>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

PaymentsConfigurationForm.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  handleFormSubmit: PropTypes.func.isRequired,
  configValues: PropTypes.object.isRequired,
  isHomeLobEnabledForOrg: PropTypes.bool.isRequired,
  paymentMethodName: PropTypes.string,
  isLastEnabledPaymentMethod: PropTypes.bool,
  isDialog: PropTypes.bool,
  paymentProvidersConfigured: PropTypes.array,
};

export default PaymentsConfigurationForm;
